import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Search, Bot, Building2, Map } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Library() {
  const [cities, setCities] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | city | state

  useEffect(() => {
    api.get("/cities").then(async (r) => {
      setCities(r.data);
      const first = r.data.find((c) => c.jurisdiction_type === "city") || r.data[0];
      if (first) {
        const detail = await api.get(`/cities/${first.slug}`);
        setActive(detail.data);
      }
    });
  }, []);

  const loadCity = async (slug) => {
    const { data } = await api.get(`/cities/${slug}`);
    setActive(data);
    setQ("");
  };

  const filteredCities = cities.filter((c) => {
    if (filter === "city" && c.jurisdiction_type !== "city") return false;
    if (filter === "state" && c.jurisdiction_type !== "state") return false;
    return true;
  });

  const rules = active?.rules?.filter((r) =>
    !q || r.title.toLowerCase().includes(q.toLowerCase()) || r.detail.toLowerCase().includes(q.toLowerCase()) || r.category.toLowerCase().includes(q.toLowerCase())
  ) || [];

  const cityCount = cities.filter((c) => c.jurisdiction_type === "city").length;
  const stateCount = cities.filter((c) => c.jurisdiction_type === "state").length;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="library-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Regulation library · Nationwide</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display">Rules across the U.S.</h1>
          <div className="text-sm text-slate-500 mt-1 font-mono">{cityCount} curated cities · {stateCount} state frameworks</div>
        </div>
        <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1 text-sm" data-testid="library-filter">
          {[
            { k: "all", l: "All" },
            { k: "city", l: "Cities", i: Building2 },
            { k: "state", l: "States", i: Map },
          ].map(({ k, l, i: Icon }) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              {Icon && <Icon className="w-3.5 h-3.5" />} {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 max-h-[420px] overflow-y-auto pr-1">
        {filteredCities.map((c) => (
          <button key={c.slug} data-testid={`city-${c.slug}`} onClick={() => loadCity(c.slug)}
            className={`relative aspect-[4/3] rounded-xl overflow-hidden text-left group transition ${active?.slug === c.slug ? "ring-2 ring-sky-500" : ""}`}>
            <img src={c.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 to-slate-900/20" />
            {c.jurisdiction_type === "state" && (
              <span className="absolute top-1.5 right-1.5 text-[8px] font-mono uppercase bg-slate-800/80 text-slate-100 rounded px-1 py-0.5">Statewide</span>
            )}
            <div className="relative h-full flex flex-col justify-end p-2.5 text-white">
              <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">{c.state}</div>
              <div className="font-display font-semibold text-xs leading-tight line-clamp-2">{c.name}</div>
            </div>
          </button>
        ))}
      </div>

      {active && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-slate-100 space-y-2">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-display font-semibold text-slate-900">{active.name}, {active.state}</h2>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">{active.ordinance}</div>
              </div>
              <Link to={`/app/assistant?city=${active.slug}`} data-testid={`ask-ai-${active.slug}`} className="inline-flex items-center gap-2 bg-sky-500 text-white px-3 py-2 rounded-lg text-xs hover:bg-sky-600">
                <Bot className="w-3.5 h-3.5" /> Ask AI
              </Link>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">{active.summary}</p>
            <div className="flex flex-wrap gap-4 pt-2 text-xs">
              <Chip label="Night cap" value={active.night_cap ? `${active.night_cap}/yr` : "None specified"} />
              <Chip label="Max fine" value={`$${active.penalty_max.toLocaleString()}`} />
              <Chip label="Rules tracked" value={active.rules.length} />
              <Chip label="Type" value={active.jurisdiction_type === "state" ? "Statewide" : "City ordinance"} />
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rules, categories, keywords…" data-testid="library-search"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:border-sky-500 outline-none" />
            </div>
            <div className="space-y-3">
              {rules.map((r) => (
                <div key={r.code} className="border border-slate-200 rounded-xl p-5" data-testid={`lib-rule-${r.code}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded cursor-pointer" onClick={() => { navigator.clipboard.writeText(r.code); toast.success("Code copied"); }}>{r.code}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{r.category}</span>
                  </div>
                  <div className="font-display font-semibold text-slate-900">{r.title}</div>
                  <p className="text-sm text-slate-600 mt-1">{r.detail}</p>
                </div>
              ))}
              {rules.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No rules match "{q}"</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1">
      <span className="font-mono uppercase tracking-widest text-[10px] text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}
